import * as React from "react"
import { Check, Copy, Shuffle } from "lucide-react"

import { AlbumArt } from "@/components/album-art"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { buildRequestCommand } from "@/config"
import { useCopyCommand } from "@/hooks/use-copy-command"
import { fetchRandomSong } from "@/lib/songs"
import type { SongListFilters } from "@/lib/songs"
import type { Song } from "@/types/song"

interface RandomSongDialogProps {
  filters: Pick<SongListFilters, "query" | "genres" | "ids">
  disabled?: boolean
}

export function RandomSongDialog({
  filters,
  disabled,
}: Readonly<RandomSongDialogProps>) {
  const [open, setOpen] = React.useState(false)
  const [pick, setPick] = React.useState<Song | null>(null)
  const [loading, setLoading] = React.useState(false)
  const { copiedId, copy } = useCopyCommand()

  const pickRandom = React.useCallback(() => {
    setLoading(true)
    fetchRandomSong(filters)
      .then(setPick)
      .catch(() => setPick(null))
      .finally(() => setLoading(false))
  }, [filters])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      pickRandom()
    }
  }

  const command = pick ? buildRequestCommand(pick.requestCode) : ""

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        variant="secondary"
        onClick={() => handleOpenChange(true)}
        disabled={disabled}
      >
        <Shuffle />
        랜덤 한 곡
      </Button>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>오늘은 이 곡 어때요?</DialogTitle>
          <DialogDescription>
            현재 필터 조건 안에서 무작위로 골라봤어요.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            뽑는 중...
          </p>
        )}

        {!loading && !pick && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            현재 조건에 맞는 곡이 없어요.
          </p>
        )}

        {!loading && pick && (
          <div className="flex flex-col items-center gap-4 text-center">
            <AlbumArt song={pick} className="w-40 rounded-3xl" />
            <div>
              <p className="text-lg font-medium">{pick.title}</p>
              <p className="text-sm text-muted-foreground">{pick.artist}</p>
            </div>

            <div className="flex w-full gap-2">
              <Button variant="outline" className="flex-1" onClick={pickRandom}>
                <Shuffle />
                다시 뽑기
              </Button>
              <Button
                className="flex-1"
                onClick={() => void copy(pick.id, command)}
              >
                {copiedId === pick.id ? <Check /> : <Copy />}
                신청 명령어 복사
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
